import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useDiscounts, fmtRate } from "@/lib/useDiscounts";
import OrderCountdown from "@/components/OrderCountdown";
import { getDeadline } from "@/lib/deadlines";

// Vignette d'offre tournante : un visuel par viticulteur. La nouvelle diapositive glisse
// de droite à gauche PAR-DESSUS l'ancienne (jamais de vide entre les publicités).
// Chaque clic mène à la sélection du viticulteur dans la cave.
// « tagline » sert à la plaquette publicitaire au-dessus de chaque maison dans la cave
// (inspirée du site officiel de la maison).
export const SLIDES_META = {
  "Famille Perrin": {
    image: "/assets/wines/offre-perrin.jpg",
    emblem: "/assets/wines/embleme-perrin.png",
    eyebrow: "Offre valable du 01/10 au 31/10",
    title: "L'Offre Famille Perrin — Les Crus",
    tagline: "Les crus de la Vallée du Rhône — la famille Perrin depuis 1909",
  },
  "Domaine Seguin Manuel": {
    image: "/assets/wines/offre-seguin.jpg",
    emblem: "/assets/wines/embleme-seguin.png",
    eyebrow: "Nouveau vigneron à la cave",
    title: "Domaine Seguin Manuel — Bourgogne",
    tagline: "Domaine familial — les terroirs de Bourgogne",
  },
  "Billecart-Salmon": {
    image: "/assets/wines/offre-billecart.jpg",
    emblem: "/assets/wines/embleme-billecart.png",
    eyebrow: "Nouvelle maison à la cave — Champagne",
    title: "Billecart-Salmon — Champagne",
    tagline: "L'instinct d'exception — Maison familiale fondée en 1818 à Mareuil-sur-Aÿ",
  },
  "Les Vins de Vienne": {
    image: "/assets/wines/offre-vienne.jpg",
    emblem: "/assets/wines/embleme-vienne.png",
    eyebrow: "Nouvelle maison à la cave — Rhône nord",
    title: "Les Vins de Vienne — Seyssuel",
    tagline: "Une signature très Rhône — la renaissance du vignoble antique de Seyssuel par Cuilleron, Gaillard et Villard",
  },
  "Domaine Gayda": {
    image: "/assets/wines/offre-gayda.jpg",
    emblem: "/assets/wines/embleme-gayda.png",
    eyebrow: "Nouveau domaine à la cave — Pays d'Oc",
    title: "Domaine Gayda — Chemin de Moscou",
    tagline: "L'art de l'assemblage au Pays d'Oc — le légendaire Chemin de Moscou, de la bouteille au mathusalem",
  },
  "Mas de Daumas Gassac": {
    image: "/assets/wines/offre-daumas.jpg",
    emblem: "/assets/wines/embleme-daumas.png",
    eyebrow: "Nouvelle maison à la cave — Languedoc",
    title: "Mas de Daumas Gassac — Aniane",
    tagline: "Le « Grand Cru » du Languedoc — la légende de la vallée du Gassac, en blanc et en rouge",
  },
  "Mas des Armes": {
    image: "/assets/wines/offre-mda.jpg",
    emblem: "/assets/wines/embleme-mda.png",
    eyebrow: "Nouveau domaine à la cave — Languedoc",
    title: "Mas des Armes — Aniane",
    tagline: "Terrasses de l'Hérault — les cuvées 360 et Perspectives, vins de gastronomie",
  },
  "Domaine Lafage": {
    image: "/assets/wines/offre-lafage.jpg",
    emblem: "/assets/wines/embleme-lafage.png",
    eyebrow: "Nouveau domaine à la cave — Roussillon",
    title: "Domaine Lafage — Roussillon",
    tagline: "Le Roussillon authentique — le rouge convivial du Domaine Lafage, entre mer et montagne",
  },
  "Deutz": {
    image: "/assets/wines/offre-deutz.jpg",
    emblem: "/assets/wines/embleme-deutz.png",
    eyebrow: "Promotion fin d'année — Nouvelle maison de champagne",
    title: "Champagne Deutz — Aÿ",
    tagline: "Maison fondée en 1838 — Brut Classic 35 €, son magnum 73 € et la cuvée de prestige Amour de Deutz",
  },
  "Beaujolais Nouveau": {
    image: "/assets/wines/offre-piron.jpg",
    emblem: "/assets/wines/embleme-piron.png",
    eyebrow: "Le primeur arrive — sortie le 19 novembre",
    title: "Beaujolais Nouveau 2026",
    tagline: "Domaine Piron, Villié-Morgon — 8 € la bouteille, prix dégressifs dès 6 bouteilles",
  },
  "Domaine du Vieux Télégraphe": {
    image: "/assets/wines/offre-vt.jpg",
    emblem: "/assets/wines/embleme-vt.png",
    eyebrow: "Nouveau domaine à la cave — Châteauneuf-du-Pape",
    title: "Domaine du Vieux Télégraphe",
    tagline: "La légende du plateau de la Crau depuis 1891 — La Crau 2023, Télégramme et Mégaphone, quantité limitée",
  },
};

export default function OfferBanner({ producers, onSelect, home = false }) {
  const navigate = useNavigate();
  const tiersMap = useDiscounts();
  const [fetched, setFetched] = useState(null);
  const [orderMap, setOrderMap] = useState({});
  const [idx, setIdx] = useState(0); // diapo affichée (couche du dessous)
  const [incoming, setIncoming] = useState(null);
  const [queued, setQueued] = useState(null); // diapo qui glisse par-dessus (pendant la transition)

  useEffect(() => {
    api
      .get("/wines")
      .then((res) => {
        // date d'arrivée la plus récente par maison -> la dernière maison arrivée passe en premier
        const latest = {};
        res.data.forEach((w) => {
          if (!w.producer) return;
          const t = w.created_at || "";
          if (!latest[w.producer] || t > latest[w.producer]) latest[w.producer] = t;
        });
        setOrderMap(latest);
        if (!producers) setFetched(Object.keys(latest));
      })
      .catch(() => setFetched([]));
  }, [producers]);

  const list = useMemo(
    () => [...(producers || fetched || [])].sort((a, b) => ((orderMap[b] || "") > (orderMap[a] || "") ? 1 : -1)),
    [producers, fetched, orderMap]
  );

  const slides = useMemo(
    () =>
      list.map((p) => {
        const meta = SLIDES_META[p] || { image: "/assets/wines/offre-perrin.jpg", emblem: null, eyebrow: "À la cave du Bistroquet", title: p };
        const tiers = tiersMap[p];
        const sub = tiers?.length
          ? `Remise panachée : ${tiers.map((t) => `${fmtRate(t.rate)}/bt dès ${t.threshold}`).join(" · ")} bouteilles`
          : "Découvrez sa sélection — commandez en ligne, retirez au restaurant";
        return { producer: p, ...meta, sub, deadline: getDeadline(p) };
      }),
    [list, tiersMap]
  );

  const show = (n) => {
    if (n === idx) return;
    if (incoming !== null) {
      setQueued(n); // une transition lente est en cours : la demande est mémorisée, pas perdue
      return;
    }
    setIncoming(n);
  };

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => show((idx + 1) % slides.length), 12000);
    return () => clearInterval(t);
  });

  if (!slides.length) return null;

  const eyebrowCls = home ? "eyebrow-light text-[1.9cqw]" : "eyebrow-light";
  const titleCls = home
    ? "font-display text-[4cqw] font-semibold text-[#FAF6F0] mt-[0.8cqw] leading-tight"
    : "font-display text-xl sm:text-3xl font-semibold text-[#FAF6F0] mt-1";
  const subCls = home
    ? "text-[1.9cqw] font-medium text-[#E8D8B0] mt-[1cqw] leading-snug max-w-[85%]"
    : "text-[0.65rem] sm:text-xs text-[#E8D8B0] mt-1.5 tracking-wide";

  const renderSlide = (slide) => (
    <>
      <img
        src={slide.image}
        alt={slide.title}
        className={`w-full object-cover no-frame ${home ? "" : "h-36 sm:h-48"}`}
        style={{ aspectRatio: home ? "838 / 270" : undefined, objectPosition: "center 45%" }}
      />
      <div
        className={`absolute inset-0 flex flex-col justify-center ${home ? "px-[5cqw]" : "px-5 sm:px-8"} bg-gradient-to-r from-[#1C1917]/85 via-[#1C1917]/45 to-transparent`}
        style={home ? { borderRadius: "3cqw" } : undefined}
      >
        <p className={eyebrowCls}>{slide.eyebrow}</p>
        <p className={titleCls}>{slide.title}</p>
        <p className={subCls}>{slide.sub}</p>
        {slide.deadline && (
          <div className={home ? "mt-[1.2cqw]" : "mt-2"}>
            <OrderCountdown deadline={slide.deadline} label={slide.deadline.label} variant="dark" />
          </div>
        )}
        {slide.emblem && (
          <img
            src={slide.emblem}
            alt={`Emblème ${slide.producer}`}
            className={`w-auto object-contain object-left no-frame ${home ? "h-[6cqw] mt-[1.6cqw]" : "h-7 sm:h-9 mt-2.5"}`}
            data-testid="offer-banner-emblem"
          />
        )}
      </div>
    </>
  );

  const go = (slide) => (onSelect ? onSelect(slide.producer) : navigate(`/vins?producer=${encodeURIComponent(slide.producer)}`));

  return (
    <div
      className={`relative w-full overflow-hidden text-left ${home ? "" : "rounded-2xl"}`}
      style={home ? { borderRadius: "3cqw" } : undefined}
      data-testid="offer-banner"
    >
      {/* Diapositive courante (statique, dessous) */}
      <button
        onClick={() => go(slides[idx])}
        className="relative w-full text-left cursor-pointer block"
        data-testid="offer-banner-cta"
        aria-label={`Voir la sélection ${slides[idx].producer}`}
      >
        {renderSlide(slides[idx])}
      </button>

      {/* Diapositive entrante : glisse de droite à gauche PAR-DESSUS l'ancienne */}
      {incoming !== null && (
        <motion.button
          key={slides[incoming].producer}
          onClick={() => go(slides[incoming])}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          transition={{ duration: 2.4, ease: [0.25, 1, 0.35, 1] }}
          onAnimationComplete={() => {
            setIdx(incoming);
            setIncoming(null);
            if (queued !== null && queued !== incoming) setIncoming(queued);
            setQueued(null);
          }}
          className="absolute inset-0 w-full text-left cursor-pointer"
          data-testid="offer-banner-incoming"
          aria-label={`Voir la sélection ${slides[incoming].producer}`}
        >
          {renderSlide(slides[incoming])}
        </motion.button>
      )}

      {slides.length > 1 && (
        <div className="absolute bottom-2.5 right-3.5 flex gap-1.5 z-10">
          {slides.map((s, i) => (
            <button
              key={s.producer}
              onClick={() => show(i)}
              className={`w-2 h-2 rounded-full transition-colors ${i === (incoming ?? idx) ? "bg-[#E8D8B0]" : "bg-white/40 hover:bg-white/70"}`}
              data-testid={`offer-dot-${i}`}
              aria-label={`Offre ${s.producer}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
